import React from 'react';
import {
  Box, Flex, Grid, GridItem, Text, Stat, StatLabel, StatNumber,
  StatHelpText, StatArrow, Icon, useColorModeValue, VStack,
  HStack, Avatar, Heading, Card, CardHeader, CardBody,
  Progress, CircularProgress, CircularProgressLabel, SimpleGrid
} from '@chakra-ui/react';
import {
  MdTrendingUp, MdAttachMoney, MdGroup, MdBusinessCenter,
  MdFlag, MdCheckCircle, MdWarning, MdShowChart
} from 'react-icons/md';
import DiretoriaGeralSidebar from '../Sidebar/DiretoriaGeralSidebar';
import './DiretoriaGeralDashboard.css';

const StatCard = ({ icon, label, value, change, changeType, colorScheme, progress }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.300`);

  return (
    <Box
      bg={bg}
      p={6}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      shadow="sm"
      transition="all 0.3s"
      _hover={{ shadow: 'md' }}
    >
      <Stat>
        <Flex justify="space-between" align="flex-start">
          <Box flex="1">
            <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" mt={2}>{value}</StatNumber>
            {change && (
              <StatHelpText mt={2}>
                <StatArrow type={changeType} />
                {change}
              </StatHelpText>
            )}
            {progress !== undefined && (
              <Progress value={progress} size="sm" colorScheme={colorScheme} mt={3} borderRadius="full" />
            )}
          </Box>
          <Icon as={icon} fontSize="40px" color={iconColor} />
        </Flex>
      </Stat>
    </Box>
  );
};

const DiretoriaGeralDashboard = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');

  return (
    <Flex minH="100vh" bg={bg}>
      <DiretoriaGeralSidebar />
      
      <Box
        flex="1"
        ml={{ base: 0, lg: "250px" }}
        transition="margin-left 0.3s"
      >
        <Box p={{ base: 4, md: 8 }}>
          {/* Header */}
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Heading size="lg" color={textColor}>
                Dashboard Executivo
              </Heading>
              <Text color="gray.500" mt={1}>
                Bem-vindo de volta, {userInfo?.name || 'Diretor'}!
              </Text>
            </Box>
            <HStack spacing={3}>
              <Avatar
                size="md"
                name={userInfo?.name || 'Diretor'}
                src={userInfo?.avatar || ''}
                bg="red.500"
              />
            </HStack>
          </Flex>

          {/* KPIs Grid */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            gap={6}
            mb={8}
          >
            <GridItem>
              <StatCard
                icon={MdAttachMoney}
                label="Faturamento Total"
                value="R$ 8.5M"
                change="23% YoY"
                changeType="increase"
                colorScheme="green"
                progress={75}
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdBusinessCenter}
                label="Projetos Ativos"
                value="47"
                change="12 novos este mês"
                changeType="increase"
                colorScheme="blue"
                progress={85}
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdGroup}
                label="Total Colaboradores"
                value="156"
                change="8% crescimento"
                changeType="increase"
                colorScheme="purple"
              />
            </GridItem>
            <GridItem>
              <StatCard
                icon={MdFlag}
                label="Metas Atingidas"
                value="82%"
                change="5% acima do esperado"
                changeType="increase"
                colorScheme="orange"
                progress={82}
              />
            </GridItem>
          </Grid>

          <Grid
            templateColumns={{ base: '1fr', lg: '2fr 1fr' }}
            gap={6}
          >
            {/* Department Performance */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Performance por Departamento</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text>Comercial</Text>
                        <Text fontWeight="bold">92%</Text>
                      </HStack>
                      <Progress value={92} colorScheme="green" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text>Jurídico</Text>
                        <Text fontWeight="bold">87%</Text>
                      </HStack>
                      <Progress value={87} colorScheme="blue" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text>Contábil</Text>
                        <Text fontWeight="bold">78%</Text>
                      </HStack>
                      <Progress value={78} colorScheme="purple" borderRadius="full" />
                    </Box>
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text>Administrativo</Text>
                        <Text fontWeight="bold">95%</Text>
                      </HStack>
                      <Progress value={95} colorScheme="orange" borderRadius="full" />
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Goals Overview */}
            <GridItem>
              <Card bg={cardBg}>
                <CardHeader>
                  <Heading size="md">Metas Trimestrais</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={6}>
                    <Box textAlign="center">
                      <CircularProgress value={82} size="120px" color="green.400" thickness="8px">
                        <CircularProgressLabel fontSize="xl" fontWeight="bold">82%</CircularProgressLabel>
                      </CircularProgress>
                      <Text mt={3} fontSize="sm" color="gray.500">Meta Geral Q3 2024</Text>
                    </Box>
                    <SimpleGrid columns={2} spacing={4} w="full">
                      <Box textAlign="center">
                        <Icon as={MdCheckCircle} fontSize="24px" color="green.500" mb={1} />
                        <Text fontSize="2xl" fontWeight="bold">14</Text>
                        <Text fontSize="xs" color="gray.500">Concluídas</Text>
                      </Box>
                      <Box textAlign="center">
                        <Icon as={MdWarning} fontSize="24px" color="orange.500" mb={1} />
                        <Text fontSize="2xl" fontWeight="bold">3</Text>
                        <Text fontSize="xs" color="gray.500">Em Risco</Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>

          {/* Strategic Initiatives */}
          <Box mt={6}>
            <Card bg={cardBg}>
              <CardHeader>
                <Heading size="md">Iniciativas Estratégicas</Heading>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  <Box p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <HStack mb={2}>
                      <Icon as={MdTrendingUp} color="blue.500" />
                      <Text fontWeight="medium">Expansão Digital</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={2}>Implementação de novas ferramentas digitais</Text>
                    <Progress value={65} size="sm" colorScheme="blue" borderRadius="full" />
                    <Text fontSize="xs" color="gray.500" mt={1}>65% completo</Text>
                  </Box>
                  <Box p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <HStack mb={2}>
                      <Icon as={MdGroup} color="green.500" />
                      <Text fontWeight="medium">Programa de Talentos</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={2}>Desenvolvimento de lideranças</Text>
                    <Progress value={40} size="sm" colorScheme="green" borderRadius="full" />
                    <Text fontSize="xs" color="gray.500" mt={1}>40% completo</Text>
                  </Box>
                  <Box p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <HStack mb={2}>
                      <Icon as={MdShowChart} color="purple.500" />
                      <Text fontWeight="medium">Otimização de Processos</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={2}>Melhoria contínua operacional</Text>
                    <Progress value={80} size="sm" colorScheme="purple" borderRadius="full" />
                    <Text fontSize="xs" color="gray.500" mt={1}>80% completo</Text>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default DiretoriaGeralDashboard;
